import {
  ItemProps,
  SectionProps,
  TableView,
} from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FC, memo, useMemo } from 'react';
import { StyleSheet } from 'react-native';

const useSectionStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        title: {
          marginLeft: rpx(15),
          marginBottom: rpx(5),
        },
        titleText: {
          color: '#999',
          fontFamily: FontFamily.NotoSans.Medium,
        },
        content: {
          backgroundColor: '#fff',
          borderRadius: rpx(16),
        },
        section: {
          marginTop: rpx(15),
          marginRight: rpx(15),
          marginBottom: rpx(15),
          marginLeft: rpx(15),
        },
        // Sits just under the card it explains, indented to the same gutter
        // as the section title. Horizontal margins are relative to the
        // section box, which is already inset by rpx(15).
        tail: {
          marginTop: rpx(8),
          marginRight: rpx(5),
          marginLeft: rpx(15),
        },
      }),
    [rpx],
  );
};

const useCellStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        separator: {
          left: rpx(40),
        },
        cellContainer: {
          height: rpx(54),
          alignItems: 'center',
        },
        title: {
          marginLeft: rpx(8),
          top: rpx(2),
        },
        titleText: {
          fontFamily: FontFamily.NotoSans.Regular,
          fontSize: rpx(16),
          color: '#333',
        },
        detailText: {
          fontFamily: FontFamily.NotoSans.Thin,
          color: '#666',
          fontSize: rpx(12),
        },
      }),
    [rpx],
  );
};

export const Section: FC<SectionProps> = memo(
  ({
    titleStyle,
    titleTextStyle,
    contentStyle,
    style,
    tailStyle,
    ...props
  }) => {
    const styles = useSectionStyles();
    return (
      <TableView.Section
        titleStyle={[styles.title, titleStyle]}
        titleTextStyle={[styles.titleText, titleTextStyle]}
        contentStyle={[styles.content, contentStyle]}
        style={[styles.section, style]}
        tailStyle={[styles.tail, tailStyle]}
        {...props}
      />
    );
  },
);
Section.displayName = 'TableSection';

export const Cell: FC<ItemProps> = memo(
  ({
    separatorStyle,
    cellContainerStyle,
    titleStyle,
    titleTextStyle,
    detailTextStyle,
    ...props
  }) => {
    const styles = useCellStyles();
    return (
      <TableView.Item
        separatorStyle={[styles.separator, separatorStyle]}
        cellContainerStyle={[styles.cellContainer, cellContainerStyle]}
        titleStyle={[styles.title, titleStyle]}
        titleTextStyle={[styles.titleText, titleTextStyle]}
        detailTextStyle={[styles.detailText, detailTextStyle]}
        {...props}
      />
    );
  },
);
Cell.displayName = 'TableItem';

const useCell2Styles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        separator: {
          left: 0,
        },
        cellContainer: {
          height: rpx(54),
          alignItems: 'center',
        },
        title: {
          marginLeft: rpx(8),
          top: rpx(2),
        },
        titleText: {
          fontFamily: FontFamily.NotoSans.Regular,
          fontSize: rpx(18),
          lineHeight: rpx(22),
          color: '#333',
        },
        detailText: {
          fontFamily: FontFamily.NotoSans.Light,
          color: '#333',
          fontSize: rpx(16),
          lineHeight: rpx(20),
        },
      }),
    [rpx],
  );
};

export const Cell2: FC<ItemProps> = memo(
  ({
    separatorStyle,
    cellContainerStyle,
    titleStyle,
    titleTextStyle,
    detailTextStyle,
    ...props
  }) => {
    const styles = useCell2Styles();
    return (
      <TableView.Item
        separatorStyle={[styles.separator, separatorStyle]}
        cellContainerStyle={[styles.cellContainer, cellContainerStyle]}
        titleStyle={[styles.title, titleStyle]}
        titleTextStyle={[styles.titleText, titleTextStyle]}
        detailTextStyle={[styles.detailText, detailTextStyle]}
        {...props}
      />
    );
  },
);
Cell.displayName = 'TableItem';
